import { RuleMode } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/rules';
import type { TriggersFilters } from '@cobliteam/api-lib/src/dashboard/alerts/utils';
import { checklistFieldCompliance } from '@cobliteam/api-lib/src/dashboard/checklist/compliance';
import type { ChecklistField } from '@cobliteam/api-lib/src/dashboard/checklist/Definitions/Types';
import type { Preferences } from '@cobliteam/common-types';
import { usePermissions } from '@cobliteam/permissions';
import { useGetTriggers } from '../useEntity/useAlerts';
import { useTemplateChecklists } from '../useEntity/useChecklist/useChecklist';
import useFeatureFlags from '../useFeatureFlags';
import useLocalStorage from '../useLocalStorage';
import { alertHookConfig, hasRuleOfEvent } from './alertHookConfig';
import { AlertHookType } from './types';
import useAlertHooksStore from './useAlertHooksStore';
import useTriggerAlertHook from './useTriggerAlertHook';

const TYPE = AlertHookType.CHECKLIST_NON_COMPLIANCE;
const config = alertHookConfig[TYPE];
const STALE_TIME = 5 * 60 * 1000;
// One page is enough to decide whether to show the suggestion; `activate` walks every page before opening.
const TEMPLATES_PAGE = { page: 0, size: 100 };
const TRIGGERS_FILTER: TriggersFilters = { page: 0, size: 200, ruleMode: RuleMode.FLEET_POLICY, isLegacy: false };

const hasComplianceField = (fields: Pick<ChecklistField, 'type' | 'content'>[] = []) => fields.some((field) => checklistFieldCompliance(field) !== null);

/**
 * Eligibility of the in-page suggestions of the non-compliance alert (checklists banner and floating
 * card). Same gates as `useTriggerAlertHook`, plus the two it leaves to the journey: the fleet has a
 * checklist with compliance, and no rule of the event yet. The api is only asked once the cheap gates pass.
 */
const useChecklistNonComplianceAlertSuggestion = () => {
  const { blueprintChecklistNonComplianceEnabled } = useFeatureFlags();
  const { isAccessAllowed } = usePermissions();
  const [preferences] = useLocalStorage<Preferences>('preferences');
  const dismissKey = config.getDismissKey({});
  const isDismissed = useAlertHooksStore((state) => state.dismissed[dismissKey] === true);
  const isActivated = useAlertHooksStore((state) => state.activated[TYPE] === true);
  const dismissWithoutOpening = useAlertHooksStore((state) => state.dismissWithoutOpening);
  const { triggerAlertHook } = useTriggerAlertHook();

  const passesCheapGates =
    blueprintChecklistNonComplianceEnabled && isAccessAllowed('config-and-view:custom-alerts') && preferences?.hideAlertHookSuggestions !== true && !isDismissed && !isActivated;

  const { data: templates } = useTemplateChecklists(TEMPLATES_PAGE, { enabled: passesCheapGates, staleTime: STALE_TIME });
  const hasComplianceTemplate = (templates?.page.content ?? []).some((template) => hasComplianceField(template.fields));

  const { data: triggers } = useGetTriggers(TRIGGERS_FILTER, { enabled: passesCheapGates && hasComplianceTemplate, staleTime: STALE_TIME, meta: {} });
  const hasRule = !triggers || hasRuleOfEvent(triggers.content, config.eventNameIdentifier);

  // Not forced: eligibility already passed the opt-out and dismissal gates, and the hook's dedup walks
  // every page of rules, so a rule beyond the first page above still keeps the modal from opening.
  const activate = () => triggerAlertHook(TYPE, {});
  const dismiss = () => dismissWithoutOpening(TYPE, {});

  return { isEligible: passesCheapGates && hasComplianceTemplate && !hasRule, activate, dismiss };
};

export default useChecklistNonComplianceAlertSuggestion;
